import { useRouter } from 'next/router'
import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { POPULAR_KEYWORDS } from '@/common/constants/popularKeywords'
import { ResponsiveSizeV2 } from '@/components/atomic/_enums'
import GlobalFooter from '@/components/atomic/organisms/GlobalFooter/screen'
import { FooterSectionText } from '@/components/atomic/organisms/GlobalFooter/helpers/types/footer'
import GlobalHeader from '@/components/atomic/organisms/GlobalHeader'
import GlobalNavBar, { GlobalNavBarTabKey } from '@/components/atomic/organisms/GlobalNavBar'
import SearchPopupContent from '@/components/atomic/organisms/SearchPopupContent'
import { IconPack } from '@/components/cell/atoms/Icon/enum'
import COLOR from '@/components/cell/designTokens/color'
import { isMobile, postAmplitude, postGA } from '@/core/lib'
import { useTranslation } from '@/lang'
import theme from '@/theme'
import { useLocales } from '@/shared/hooks/useLocales'
import { clientTreatmentCategoryFilterService } from '@/features/treatment-category-filter/api/treatment-category-filter.service'
import { TreatmentCategory } from '@/features/treatment-category-filter/types/treatment-filter.type'
import { createSignInUrl } from '@/shared/utils/url-utils'

export interface Props {
  userProfileImage?: string
  isOpenSearch?: boolean
  isRootScreen?: boolean
  isGlobalHeaderHidden?: boolean
  pageBanner?: React.ReactNode
  useGnb?: boolean
  useFooter?: boolean
  useSearch?: boolean
  useSignIn?: boolean
  useHeaderLeftIcon?: boolean
  selectedGnbTabKey?: GlobalNavBarTabKey
  onChangeOpenSearchState?: (isOpenSearch: boolean) => void
  bottomMargin?: string
  bottomPaddingToAdd?: string
  className?: string
}

const Screen = ({
  userProfileImage,
  useGnb,
  useFooter = true,
  selectedGnbTabKey,
  isRootScreen = false,
  isOpenSearch = false,
  isGlobalHeaderHidden = false,
  useSearch = true,
  useSignIn = true,
  useHeaderLeftIcon = true,
  pageBanner,
  onChangeOpenSearchState,
  children,
  bottomMargin,
  bottomPaddingToAdd,
  className,
}: PropsWithChildren<Props>): JSX.Element => {
  const { t } = useTranslation()
  const router = useRouter()
  const [isShowSearchPopup, setIsShowSearchPopup] = useState(isOpenSearch)
  const { locale } = useLocales()
  const popularKeywords = POPULAR_KEYWORDS[locale]
  const [treatmentCategory, setTreatmentCategoryTags] = useState<TreatmentCategory[]>([])

  const logo = {
    url: t('common.header.logoImgUrl'),
    alt: 'gangnamunni_logo',
  }

  const onClickLogo = () => {
    void router.push('/')
  }

  const handleSearch = (q: string, action: 'click_button' | 'press_enter', type: 'input' | 'popular') => {
    if (q !== '') {
      void router.push(`/search?q=${q}`)

      switch (type) {
        case 'input':
          postGA('search_keyword', 'click', q)
          break
        case 'popular':
          postGA('search_treatment', 'click', q)
          break
      }
      postAmplitude('click_search', { q, action, type })
    }
  }

  const handleClickTreatment = (treatment: TreatmentCategory) => {
    void router.push(`/search?category=${treatment.name}`)

    postGA('search_concern', 'click', treatment.name)
    postAmplitude('click_treatment_ctg_in_searchpop', {
      treatment_ctg_id: treatment.id,
      treatment_ctg_name: treatment.name,
    })
  }

  const handleClickMoreTreatment = () => {
    postAmplitude('click_more_treatment_ctg_in_searchpop')
  }
  const handleClickSearchIcon = () => {
    setIsShowSearchPopup(true)

    postAmplitude('click_search_icon_in_gnb')
  }

  const handleClickSignIn = (isSignIn: boolean) => {
    if (!isSignIn) {
      void router.push(createSignInUrl(router.asPath, locale))
      postAmplitude('click_login_in_header')
    } else {
      void router.push('/mypage')
    }
  }

  const handleClickFooter = (item: FooterSectionText) => {
    if (item.key === 'inquiry.application') {
      postGA('click_affliate', 'click')
    }
  }

  const handleClickGnbTab = (key: GlobalNavBarTabKey) => {
    const properties = {
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
    }
    const eventNames = {
      home: 'click_main_in_gnb',
      hospital: 'click_hospital_in_gnb',
      column: 'click_column_in_gnb',
      event: 'click_event_in_gnb',
      community: 'click_community_in_gnb',
      'unni-guide': 'click_unni_guide_in_gnb',
    }

    postAmplitude(eventNames[key], properties)
  }

  const closeSearchPopup = useCallback(() => {
    const isOpenSearch = false
    if (onChangeOpenSearchState) {
      onChangeOpenSearchState(isOpenSearch)
    }
    setIsShowSearchPopup(() => isOpenSearch)
  }, [onChangeOpenSearchState])

  useEffect(() => {
    setIsShowSearchPopup(isOpenSearch)
  }, [isOpenSearch])

  useEffect(() => {
    void clientTreatmentCategoryFilterService.getTreatmentCategoryFilter().then((res) => {
      setTreatmentCategoryTags(res.treatmentCategories)
    })

    const handleRouteChangeComplete = () => {
      setIsShowSearchPopup(false)
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
    }
  }, [router.events])

  return (
    <Container className={className}>
      <Page>
        <StickyHeader>
          <Banner $isHidden={!pageBanner}>{pageBanner}</Banner>
          <Header $isHidden={isGlobalHeaderHidden}>
            <GlobalHeader
              disabledSearch={isRootScreen || !useSearch}
              disabledSignIn={!useSignIn}
              label="top header"
              logo={logo}
              leftIcon={useHeaderLeftIcon && !isRootScreen ? IconPack.GENERAL_ACTION_BACK_BLACK : undefined}
              ariaLabelLeftIcon="Go Back"
              profileImageURL={userProfileImage}
              onClickSignInButton={handleClickSignIn}
              onClickLeftIcon={() => {
                if (isMobile()) {
                  if (window.history.length === 1) {
                    window.close()
                  }
                }
                if (!isRootScreen) {
                  router.back()
                }
              }}
              onClickLogo={onClickLogo}
              onClickSearchIcon={handleClickSearchIcon}
            />
          </Header>
        </StickyHeader>
        <Main id="screenMain">
          {children}
          {useFooter && (
            <GlobalFooter
              label="bottom footer"
              logo={logo}
              onClick={handleClickFooter}
              bottomMargin={bottomMargin}
              bottomPaddingToAdd={bottomPaddingToAdd}
            />
          )}
        </Main>
        <BottomNavigationBar $isHidden={!useGnb}>
          <GlobalNavBar
            selectedGnbTabKey={selectedGnbTabKey}
            text={t('common.gnb', { returnObjects: true })}
            onclick={handleClickGnbTab}
          />
        </BottomNavigationBar>
      </Page>
      <StyledSearchPopup $isShowPopup={isShowSearchPopup}>
        <SearchPopupContent
          treatmentCategory={{
            title: t('searchPopup.treatmentTitle'),
            moreText: t('treatment.seeMore'),
            list: treatmentCategory || [],
          }}
          keyword={{
            title: t('searchPopup.popularKeywordTitle'),
            list: popularKeywords || [],
          }}
          placeholder={t('searchPopup.inputPlaceholder')}
          onClickTreatment={handleClickTreatment}
          onClickMoreTreatment={handleClickMoreTreatment}
          onClickClose={closeSearchPopup}
          isShowSearchPopup={isShowSearchPopup}
          onSearch={handleSearch}
        />
      </StyledSearchPopup>
    </Container>
  )
}

const Banner = styled.div<{ $isHidden: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Main = styled.main`
  position: relative;
  background-color: ${COLOR.white};
  min-height: calc(100vh - ${theme.variables.globalHeaderHeight}px);
  display: flex;
  flex-direction: column;
`

const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${({ theme }) => theme.colors.gray200};
  &::-webkit-scrollbar {
    width: 1px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 0px;
  }
  &::-webkit-scrollbar-track {
    margin-top: 48px;
  }
`

const HEADER_Z_INDEX = 201
const SEARCH_Z_INDEX = 202 // searchPopup은 Header를 가립니다.
const FOOTER_Z_INDEX = 204 // footer는 가려지지 않습니다.

const StickyHeader = styled.div`
  z-index: ${HEADER_Z_INDEX};
  position: sticky;
  top: 0px;
`
const Header = styled.div<{ $isHidden: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`

const BottomNavigationBar = styled.footer<{ $isHidden: boolean }>`
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
  z-index: ${FOOTER_Z_INDEX};
  position: sticky;
  bottom: 0px;
`

const StyledSearchPopup = styled.div<{ $isShowPopup: boolean }>`
  z-index: ${SEARCH_Z_INDEX};
  display: ${({ $isShowPopup }) => ($isShowPopup ? 'block' : 'none')};
  position: fixed;
  width: 100%;
  height: calc(100vh + ${theme.variables.globalHeaderHeight}px);
  max-width: 100%;
  top: 0px;
  background-color: ${COLOR.white};

  // isDesktop
  @media screen and (min-width: ${ResponsiveSizeV2.MOBILE_MAX_WIDTH}px) {
    max-width: ${ResponsiveSizeV2.MOBILE_WIDTH}px;
  }
`

export default Screen
